import * as Components from '../components'
import * as Hooks from '../hooks'
import * as Stores from '../stores'
import { H4, H6, SizableText, useEvent, View, XStack, YStack } from 'tamagui'
import { router } from 'expo-router'
import { Routes as AppRoutes } from '@/src/common/enums/routes'
import { MesaCardImage } from '@/src/common/modules/CardSection'
import { DesignSystem } from '@/src/design-system'
import { formatCurrency } from '@/src/common/utils/general'
import { useEffect } from 'react'
import { GenericErrorScreen } from './GenericErrorScreen'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks/useCardAccount'
import { Api } from '@/src/store'

export function ApprovedScreen() {
  const Alert = useAlert(ApprovedScreen.name)
  const { activeApplicationId } = Hooks.useActiveApplication()
  const terms = Hooks.useTerms()
  const [getTerms, { data, isSuccess, isUninitialized, isLoading }] =
    Stores.CardApplicationApi.endpoints[
      'GET /card/onboarding/applications/:applicationId/terms'
    ].useLazyQuery()
  const [acknowledgeTerms] =
    Stores.CardApplicationApi.endpoints[
      'POST /card/onboarding/applications/:applicationId/terms/acknowledge'
    ].useMutation()

  useEffect(() => {
    if (activeApplicationId) {
      void getTerms({
        params: {
          applicationId: activeApplicationId,
        },
      })
    }
  }, [activeApplicationId, getTerms])

  /**
   * it's not enough that {@link acknowledgeTerms} `invalidatesTags` `'GET /card/accounts'` ({@link useCardAccount}'s endpoint) because we need to also await until the refetch finishes, otherwise the UI would be redirecting to the cardBenefits (card application entrypoint) screen.
   * this is essentially like [apollo's `awaitRefetchQueries`](https://www.apollographql.com/docs/react/data/mutations#options), for which rtkq offers no out-of-the-box equivalent
   */
  const refetchCardAccount = useCardAccount().refetch
  /** @see {@link refetchCardAccount} */
  const refetchCardApplication = Api.endpoints['GET /card/onboarding/applications'].useQuery(null, {
    refetchOnMountOrArgChange: true,
  }).refetch

  const handleAcknowledgeTerms = useEvent(async () => {
    if (activeApplicationId) {
      const result = await acknowledgeTerms({
        params: { applicationId: activeApplicationId },
      })
      if (result.error) {
        Alert.alert(
          'Error',
          'We had a problem processing your request. Please try again later or contact support@mesamember.com.',
        )
      } else {
        await Promise.all([refetchCardAccount(), refetchCardApplication()])
        router.push(AppRoutes.VirtualCard.route)
      }
    }
  })

  if (!(isUninitialized || isLoading || isSuccess) && !data) {
    return <GenericErrorScreen />
  }

  return (
    <View flex={1} bg="$white1" pb="$8">
      <Components.ApplicationScreen>
        <Components.ApplicationScreen.Content title="Congratulations!">
          <Components.FormHelpText>Your application has been approved</Components.FormHelpText>
          <View items="center">
            <MesaCardImage width={299} height={189} resizeMode="contain" />
          </View>
          <DesignSystem.Card>
            <YStack m="$2" gap="$4">
              <H4>The Mesa Homeowners Card</H4>
              <XStack justify="space-between">
                <SizableText>Credit Line</SizableText>
                {data && <H6>{formatCurrency(`${data.creditLimitCents}`)}</H6>}
                {!data && <DesignSystem.Skeleton.Skeleton style={{ width: 85, height: 26 }} />}
              </XStack>
              <View bg="$white4" height={1} />
              <XStack justify="space-between">
                <SizableText>APR</SizableText>
                {data && <H6>{data.interestRatePercentage}%</H6>}
                {!data && <DesignSystem.Skeleton.Skeleton style={{ width: 55, height: 26 }} />}
              </XStack>
              <Components.FormHelpText size="$2" color="$black10">
                Estimated arrival is 7 - 10 business days.
              </Components.FormHelpText>
              <Components.FormHelpText size="$2" color="$black10">
                You can access your virtual Mesa card immediately by using the virtual card directly
                in-app.
              </Components.FormHelpText>
              <Components.FormHelpLink size="$2" url={terms.cardholderAgreement.link}>
                Terms and Conditions Apply. See Mesa Cardholder Agreement.
              </Components.FormHelpLink>
            </YStack>
          </DesignSystem.Card>
        </Components.ApplicationScreen.Content>
        <Components.ApplicationScreen.SubmitButton
          onPress={() => void handleAcknowledgeTerms()}
          title="Go to Mesa Card Now"
        />
      </Components.ApplicationScreen>
    </View>
  )
}
